import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Spinner from "../../../components/UI/Spinner";
import ErrorModal from "../../../components/UI/Error";
import { priceSchema } from "../../../ZodSchemas/PricingSchema";
import { pricingService } from "../../../services";

const PriceForm = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(isEditing);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage]=useState(null)
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(priceSchema),
    defaultValues: {
      name: "",
      price: 0,
      uploads: 0,
      info: [""],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "info",
  });

  useEffect(() => {
    if (isEditing) {
      const fetchPrice = async () => {
        setError(false);
        try {
          const priceData = await pricingService.getPricingPlan(id);
          Object.keys(priceData).forEach((key) =>
            setValue(key, priceData[key])
          );
        } catch (error) {
          setError(true);
          console.error("Failed to fetch price data", error);
        } finally {
          setLoading(false);
        }
      };
      fetchPrice();
    }
  }, [id, isEditing, setValue]);

  const onSubmit = async (data) => {
    setUpdating(true);
    setError(false)
    setErrorMessage(null)
    try {
      if (isEditing) {
        await pricingService.updatePricingPlan(id, data);
      } else {
        await pricingService.createPricingPlan(data);
      }
    } catch (error) {
      setError(true)
      if(isEditing){
        setErrorMessage({title: "Updating", message:"update the plan details"})
      }else{
        setErrorMessage({title: "Creating", message:"create new plan"})
      }
    } finally {
      setUpdating(false);
    }
  };

  if (loading)
    return (
      <div className="grid place-content-center h-[60vh]">
        <Spinner />
      </div>
    );
  if (error)
    return (
      <ErrorModal
        isOpen={error}
        onClose={() => setError(false)}
        title={`Error ${errorMessage?.title || "Fetching"} Plan`}
        message={
          `There is error while trying to ${errorMessage?.message || "fetch the plan details"}. Please check your internet contivities and refresh this page to try again`
        }
      />
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <input
          {...register("name")}
          className="border p-2 w-full"
          placeholder="Price Name"
        />
        {errors.name && <p className="text-red-500">{errors.name.message}</p>}
      </div>

      <div>
        <input
          type="number"
          {...register("price", { valueAsNumber: true })}
          className="border p-2 w-full"
          placeholder="Plan Price"
        />
        {errors.price && <p className="text-red-500">{errors.price.message}</p>}
      </div>

      <div>
        <input
          type="number"
          {...register("uploads", { valueAsNumber: true })}
          className="border p-2 w-full"
          placeholder="Price Uploads"
        />
        {errors.uploads && (
          <p className="text-red-500">{errors.uploads.message}</p>
        )}
      </div>

      {/* Info Fields */}
      <div className="text-left ">
        <h6 className="text-left text-gray-300 text-sm -mb-8 ml-1">Adds On</h6>
        <br />
        {fields.map((field, index) => (
          <div key={field.id} className="flex gap-2 items-center">
            <input
              {...register(`info.${index}`)}
              className="border p-2 w-full"
              placeholder="Input new adds on..."
            />
            <button
              type="button"
              className="bg-red-500 text-white px-2 py-1 rounded-lg mt-3"
              onClick={() => remove(index)}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="bg-green-500 text-white p-2 mt-2"
          onClick={() => append("")}>
          Add New
        </button>
      </div>

      <button type="submit" className="formSubmitBtn" disabled={updating}>
        {updating
          ? isEditing
            ? "Updating..."
            : "Creating..."
          : isEditing
          ? "Update Price"
          : "Create Price"}
      </button>
    </form>
  );
};

export default PriceForm;
